import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { REACT_APP_API_URL } from "../../../../env";
import { showCmsErrorAlert, showCmsSuccessAlert } from "../../../../Common/cmsAlerts";

const API_BASE = REACT_APP_API_URL.replace(/\/$/, "");
const apiUrl = (path) => `${API_BASE}${path}`;

const defaultPage = {
  title: "",
  subtitle: "",
  heroImage: "",
  heroAlt: "",
};

const emptyList = [""];

const defaultForm = {
  intTourID: 0,
  vchTitle: "",
  vchLocation: "",
  intPrice: "",
  vchDuration: "",
  intGuests: "",
  bitFeatured: false,
  intDiscount: 0,
  vchDescription: "",
  primaryImage: "",
  bitIsActive: true,
  badge: "",
  summary: "",
  highlights: emptyList,
  inclusions: emptyList,
  exclusions: emptyList,
  itinerary: emptyList,
};

const assetUrl = (path) => {
  if (!path) return "";
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  if (path.startsWith("/tourPackageUploads/")) return `${API_BASE}${path}`;
  return path;
};

const normalizeTextList = (value, fallback = emptyList) => {
  const list = Array.isArray(value) ? value.map((item) => String(item ?? "")) : [];
  return list.length ? list : [...fallback];
};

const buildFormState = (tour) => ({
  intTourID: tour?.intTourID || 0,
  vchTitle: tour?.title || tour?.vchTitle || "",
  vchLocation: tour?.location || tour?.vchLocation || "",
  intPrice: tour?.price ?? tour?.intPrice ?? "",
  vchDuration: tour?.duration || tour?.vchDuration || "",
  intGuests: tour?.guests ?? tour?.intGuests ?? "",
  bitFeatured: !!(tour?.featured ?? tour?.bitFeatured),
  intDiscount: tour?.discount ?? tour?.intDiscount ?? 0,
  vchDescription: tour?.description || tour?.vchDescription || "",
  primaryImage: tour?.primaryImage || "",
  bitIsActive: tour?.isActive ?? tour?.bitIsActive ?? true,
  badge: tour?.badge || "",
  summary: tour?.summary || "",
  highlights: normalizeTextList(tour?.highlights, emptyList),
  inclusions: normalizeTextList(tour?.inclusions, emptyList),
  exclusions: normalizeTextList(tour?.exclusions, emptyList),
  itinerary: normalizeTextList(tour?.itinerary, emptyList),
});

const trimTextList = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);

const formatPrice = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

const ListEditor = ({ title, subtitle, items, setItems, multiline = false, placeholder }) => {
  const updateItem = (index, value) => {
    setItems((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
  };

  const addItem = () => {
    setItems((current) => [...current, ""]);
  };

  const removeItem = (index) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <div className="card border-0 bg-light h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h6 className="mb-1">{title}</h6>
            <p className="text-muted small mb-0">{subtitle}</p>
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={addItem}>
            Add
          </button>
        </div>

        <div className="row g-3">
          {items.map((item, index) => (
            <div className="col-12" key={`${title}-${index}`}>
              <label className="form-label">{title} {index + 1}</label>
              {multiline ? (
                <textarea
                  className="form-control"
                  rows="3"
                  value={item}
                  placeholder={placeholder}
                  onChange={(event) => updateItem(index, event.target.value)}
                />
              ) : (
                <input
                  className="form-control"
                  value={item}
                  placeholder={placeholder}
                  onChange={(event) => updateItem(index, event.target.value)}
                />
              )}
              {items.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger px-0 mt-1"
                  onClick={() => removeItem(index)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ImageField = ({
  label,
  file,
  onFileChange,
  pathValue,
  onPathChange,
  previewAlt,
  helperText,
  detailsLabel = "Use an existing image path instead",
}) => (
  <div>
    <label className="form-label">{label}</label>
    <input
      type="file"
      accept="image/*"
      className="form-control"
      onChange={(event) => onFileChange(event.target.files?.[0] || null)}
    />
    {file ? (
      <div className="form-text">Selected file: {file.name}</div>
    ) : helperText ? (
      <div className="form-text">{helperText}</div>
    ) : null}

    {!file && pathValue && (
      <div className="mt-3">
        <div className="small text-muted mb-2">Current image</div>
        <img
          src={assetUrl(pathValue)}
          alt={previewAlt}
          className="img-fluid rounded border"
          style={{ maxHeight: 200, objectFit: "cover" }}
        />
      </div>
    )}

    <details className="mt-3">
      <summary style={{ cursor: "pointer" }} className="small text-primary">
        {detailsLabel}
      </summary>
      <input
        className="form-control mt-2"
        placeholder="Paste an existing image path"
        value={pathValue}
        onChange={(event) => onPathChange(event.target.value)}
      />
    </details>
  </div>
);

const PageSettingsForm = ({ page, onSuccess }) => {
  const [form, setForm] = useState(defaultPage);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      title: page?.title || "",
      subtitle: page?.subtitle || "",
      heroImage: page?.heroImage || "",
      heroAlt: page?.heroAlt || "",
    });
    setHeroImageFile(null);
  }, [page]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const formData = new FormData();
    formData.append("pageTitle", form.title);
    formData.append("heroSubtitle", form.subtitle);
    formData.append("heroAlt", form.heroAlt);
    formData.append("heroImage", form.heroImage);
    if (heroImageFile) formData.append("heroImage", heroImageFile);

    try {
      await axios.put(apiUrl("/api/tour/page-settings"), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const refreshed = await onSuccess?.();
      if (refreshed !== false) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header fw-bold">Page Hero</div>
      <div className="card-body">
        <p className="text-muted mb-3">
          This controls the large banner visitors see at the top of the Tours page.
        </p>

        <form onSubmit={submit}>
          <div className="row g-4 align-items-start">

            {/* Left Column */}
            <div className="col-lg-6">

              <div className="mb-3">
                <label className="form-label">Hero Heading</label>

                <input
                  className="form-control"
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Supporting Line</label>

                <textarea
                  className="form-control"
                  rows="3"
                  value={form.subtitle}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      subtitle: event.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="form-label">Image Description</label>

                <input
                  className="form-control"
                  value={form.heroAlt}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      heroAlt: event.target.value,
                    }))
                  }
                  placeholder="Optional description for screen readers"
                />
              </div>

            </div>

            {/* Right Column */}
            <div className="col-lg-6">

              <ImageField
                label="Hero Image"
                file={heroImageFile}
                onFileChange={setHeroImageFile}
                pathValue={form.heroImage}
                onPathChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    heroImage: value,
                  }))
                }
                previewAlt={form.title || "Tour page hero"}
                helperText="Upload the image for the page banner."
                detailsLabel="Use an existing image path for the hero"
              />

            </div>

          </div>

          <div className="d-flex justify-content-end mt-4">
            <button className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Hero"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TourSummaryView = ({ tour }) => {
  const gallery = Array.isArray(tour?.images)
    ? tour.images
    : String(tour?.vchImage || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  const cardImage = tour?.primaryImage || "";

  return (
    <div className="row g-4">
      <div className="col-lg-5">
        {cardImage && (
          <img
            src={assetUrl(cardImage)}
            alt={tour?.title || "Tour image"}
            className="img-fluid rounded border mb-3"
            style={{ maxHeight: 280, objectFit: "cover", width: "100%" }}
          />
        )}

        <div className="card">
          <div className="card-body">
            <h6 className="mb-3">Package Snapshot</h6>
            <div className="small text-muted mb-1">Location</div>
            <div className="mb-3">{tour?.location || tour?.vchLocation || "-"}</div>

            <div className="small text-muted mb-1">Price</div>
            <div className="mb-3">{formatPrice(tour?.price || tour?.intPrice)}</div>

            <div className="small text-muted mb-1">Duration</div>
            <div className="mb-3">{tour?.duration || tour?.vchDuration || "-"}</div>

            <div className="small text-muted mb-1">Guest Capacity</div>
            <div className="mb-3">{tour?.guests || tour?.intGuests || "-"}</div>

            <div className="small text-muted mb-1">Website Status</div>
            <div className="mb-3">
              <span className={`badge ${tour?.isActive || tour?.bitIsActive ? "bg-success" : "bg-secondary"}`}>
                {tour?.isActive || tour?.bitIsActive ? "Published" : "Hidden"}
              </span>
              {(tour?.featured || tour?.bitFeatured) && (
                <span className="badge bg-warning text-dark ms-2">Featured</span>
              )}
            </div>

            {(tour?.badge || "").trim() && (
              <>
                <div className="small text-muted mb-1">Label</div>
                <div>{tour.badge}</div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="col-lg-7">
        <h4 className="mb-1">{tour?.title || tour?.vchTitle || "Untitled Package"}</h4>
        {tour?.summary && <p className="text-muted">{tour.summary}</p>}
        {tour?.description && <p>{tour.description}</p>}

        {Array.isArray(tour?.highlights) && tour.highlights.length > 0 && (
          <div className="mb-4">
            <h6>Highlights</h6>
            <div className="d-flex flex-wrap gap-2">
              {tour.highlights.map((item) => (
                <span key={item} className="badge bg-light text-dark border">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="row g-4">
          {Array.isArray(tour?.inclusions) && tour.inclusions.length > 0 && (
            <div className="col-md-6">
              <h6>Included</h6>
              <ul className="mb-0">
                {tour.inclusions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(tour?.exclusions) && tour.exclusions.length > 0 && (
            <div className="col-md-6">
              <h6>Not Included</h6>
              <ul className="mb-0">
                {tour.exclusions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {Array.isArray(tour?.itinerary) && tour.itinerary.length > 0 && (
          <div className="mt-4">
            <h6>Itinerary</h6>
            <div className="d-flex flex-column gap-2">
              {tour.itinerary.map((item, index) => (
                <div key={`${item}-${index}`} className="border rounded p-2 bg-light">
                  <span className="fw-semibold me-2">Step {index + 1}</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}

        {gallery.length > 1 && (
          <div className="mt-4">
            <h6>Gallery</h6>
            <div className="d-flex flex-wrap gap-2">
              {gallery.map((image, index) => (
                <img
                  key={`${image}-${index}`}
                  src={assetUrl(image)}
                  alt={`Gallery ${index + 1}`}
                  className="rounded border"
                  style={{ width: 120, height: 82, objectFit: "cover" }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TourForm = ({ editData, onSuccess }) => {
  const [form, setForm] = useState(defaultForm);
  const [primaryImageFile, setPrimaryImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(buildFormState(editData));
    setPrimaryImageFile(null);
    setGalleryFiles([]);
    const nextGallery = Array.isArray(editData?.images)
      ? editData.images
      : String(editData?.vchImage || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    setGalleryImages(nextGallery);
  }, [editData]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const removeGalleryImage = (indexToRemove) => {
    setGalleryImages((current) =>
      current.filter((_, imageIndex) => imageIndex !== indexToRemove)
    );
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!String(form.vchTitle || "").trim()) {
      alert("Please enter the package name.");
      return;
    }

    if (!String(form.vchLocation || "").trim()) {
      alert("Please enter the location.");
      return;
    }

    if (!String(form.vchDuration || "").trim()) {
      alert("Please enter the duration.");
      return;
    }

    if (!String(form.vchDescription || "").trim()) {
      alert("Please enter the short description.");
      return;
    }

    if (!editData && galleryFiles.length === 0) {
      alert("Please upload at least one tour image.");
      return;
    }

    setSaving(true);

    const formData = new FormData();
    formData.append("vchTitle", form.vchTitle);
    formData.append("vchLocation", form.vchLocation);
    formData.append("intPrice", String(form.intPrice || 0));
    formData.append("vchDuration", form.vchDuration);
    formData.append("intGuests", String(form.intGuests || 0));
    formData.append("bitFeatured", form.bitFeatured ? "1" : "0");
    formData.append("intDiscount", String(form.intDiscount || 0));
    formData.append("vchDescription", form.vchDescription);
    formData.append("primaryImage", form.primaryImage || "");
    formData.append("bitIsActive", form.bitIsActive ? "1" : "0");
    formData.append("badge", form.badge);
    formData.append("summary", form.summary);
    formData.append("highlights", JSON.stringify(trimTextList(form.highlights)));
    formData.append("inclusions", JSON.stringify(trimTextList(form.inclusions)));
    formData.append("exclusions", JSON.stringify(trimTextList(form.exclusions)));
    formData.append("itinerary", JSON.stringify(trimTextList(form.itinerary)));
    formData.append("keepGalleryImages", JSON.stringify(galleryImages));

    if (primaryImageFile) formData.append("primaryImage", primaryImageFile);

    galleryFiles.forEach((file) => formData.append("images", file));

    try {
      if (editData?.intTourID) {
        await axios.post(apiUrl(`/api/tour/update/${editData.intTourID}`), formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(apiUrl("/api/tour/add"), formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const refreshed = await onSuccess?.();
      if (refreshed !== false) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="alert alert-info">
        Each package here becomes one tour card on the public Tours page and one detail page for visitors.
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Package Name *</label>
          <input
            className="form-control"
            value={form.vchTitle}
            onChange={(event) => setField("vchTitle", event.target.value)}
            placeholder="Example: Patratu Valley Day Tour"
            required
          />
          <div className="form-text">Use the public package name visitors will recognize.</div>
        </div>

        <div className="col-md-6">
          <label className="form-label">Destination / Base Location *</label>
          <input
            className="form-control"
            value={form.vchLocation}
            onChange={(event) => setField("vchLocation", event.target.value)}
            placeholder="Example: Ranchi"
            required
          />
          <div className="form-text">Add the city or place this package starts from or covers.</div>
        </div>

        <div className="col-md-3">
          <label className="form-label">Price Per Person</label>
          <input
            type="number"
            className="form-control"
            value={form.intPrice}
            onChange={(event) => setField("intPrice", event.target.value)}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Duration Text *</label>
          <input
            className="form-control"
            value={form.vchDuration}
            onChange={(event) => setField("vchDuration", event.target.value)}
            placeholder="Example: 1 Day"
            required
          />
          <div className="form-text">Write this exactly as it should appear on the website.</div>
        </div>

        <div className="col-md-3">
          <label className="form-label">Guest Capacity</label>
          <input
            type="number"
            className="form-control"
            value={form.intGuests}
            onChange={(event) => setField("intGuests", event.target.value)}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Discount (%)</label>
          <input
            type="number"
            className="form-control"
            value={form.intDiscount}
            onChange={(event) => setField("intDiscount", event.target.value)}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Label</label>
          <input
            className="form-control"
            value={form.badge}
            onChange={(event) => setField("badge", event.target.value)}
            placeholder="Example: Weekend Circuit"
          />
          <div className="form-text">Optional small label shown above the package.</div>
        </div>

        <div className="col-md-6">
          <label className="form-label">Card Summary</label>
          <input
            className="form-control"
            value={form.summary}
            onChange={(event) => setField("summary", event.target.value)}
            placeholder="Optional short line for the package card"
          />
        </div>

        <div className="col-12">
          <label className="form-label">Short Description *</label>
          <textarea
            className="form-control"
            rows="4"
            value={form.vchDescription}
            onChange={(event) => setField("vchDescription", event.target.value)}
            placeholder="Write the overview visitors should read first"
            required
          />
          <div className="form-text">Keep it short and visitor-friendly. Example: One-day scenic circuit with guided stops.</div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3">
            <div className="row g-3">
              <div className="col-md-6">
                <ImageField
                  label="Primary Card Image"
                  file={primaryImageFile}
                  onFileChange={setPrimaryImageFile}
                  pathValue={form.primaryImage}
                  onPathChange={(value) => setField("primaryImage", value)}
                  previewAlt={form.vchTitle || "Primary card image"}
                  helperText="Used only on the tour package card."
                  detailsLabel="Use an existing image path for card image"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Upload Gallery Images *</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  multiple
                  onChange={(event) => setGalleryFiles(Array.from(event.target.files || []))}
                />
                <div className="form-text">
                  Required when adding a new package. New uploads are appended to the existing gallery.
                </div>
              </div>

              <div className="col-md-3">
                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={form.bitFeatured}
                    onChange={(event) => setField("bitFeatured", event.target.checked)}
                  />
                  <span className="form-check-label ms-2">Show as Featured Package</span>
                </label>
              </div>

              <div className="col-md-3">
                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={form.bitIsActive}
                    onChange={(event) => setField("bitIsActive", event.target.checked)}
                  />
                  <span className="form-check-label ms-2">Publish on Website</span>
                </label>
              </div>
            </div>

            {galleryImages.length > 0 && (
              <div className="mt-3">
                <div className="small text-muted mb-2">Current gallery</div>
                <div className="d-flex flex-wrap gap-2">
                  {galleryImages.map((image, index) => (
                    <div key={`${image}-${index}`} className="position-relative">
                      <img
                        src={assetUrl(image)}
                        alt={`Current tour ${index + 1}`}
                        className="rounded border"
                        style={{ width: 120, height: 82, objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 px-1 py-0"
                        onClick={() => removeGalleryImage(index)}
                        aria-label={`Remove gallery image ${index + 1}`}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {galleryFiles.length > 0 && (
              <div className="mt-3">
                <div className="small text-muted mb-2">New gallery preview</div>
                <div className="d-flex flex-wrap gap-2">
                  {galleryFiles.map((file, index) => (
                    <img
                      key={`${file.name}-${index}`}
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="rounded border border-success"
                      style={{ width: 120, height: 82, objectFit: "cover" }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <ListEditor
            title="Highlight"
            subtitle="Short standout points for this package."
            items={form.highlights}
            setItems={(updater) =>
              setForm((current) => ({
                ...current,
                highlights:
                  typeof updater === "function" ? updater(current.highlights) : updater,
              }))
            }
            placeholder="Example: Guided mine tour"
          />
        </div>

        <div className="col-md-6">
          <ListEditor
            title="Included Item"
            subtitle="What the package covers for the visitor."
            items={form.inclusions}
            setItems={(updater) =>
              setForm((current) => ({
                ...current,
                inclusions:
                  typeof updater === "function" ? updater(current.inclusions) : updater,
              }))
            }
            placeholder="Example: Transportation"
          />
        </div>

        <div className="col-md-6">
          <ListEditor
            title="Excluded Item"
            subtitle="What the visitor should arrange separately."
            items={form.exclusions}
            setItems={(updater) =>
              setForm((current) => ({
                ...current,
                exclusions:
                  typeof updater === "function" ? updater(current.exclusions) : updater,
              }))
            }
            placeholder="Example: Personal shopping"
          />
        </div>

        <div className="col-md-6">
          <ListEditor
            title="Itinerary Step"
            subtitle="The step-by-step tour plan shown on the detail page."
            items={form.itinerary}
            setItems={(updater) =>
              setForm((current) => ({
                ...current,
                itinerary:
                  typeof updater === "function" ? updater(current.itinerary) : updater,
              }))
            }
            placeholder="Example: 8:00 AM - Departure from Ranchi"
            multiline
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button className="btn btn-success" disabled={saving}>
          {saving ? "Saving..." : "Save Package"}
        </button>
      </div>
    </form>
  );
};

const AdminTours = () => {
  const [tours, setTours] = useState([]);
  const [page, setPage] = useState(defaultPage);
  const [showArchived, setShowArchived] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState("add");
  const [selectedTour, setSelectedTour] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadTours = useCallback(async (silent = false) => {
    setLoading(true);
    try {
      const res = await axios.get(apiUrl("/api/tour/list"), {
        params: { archived: showArchived ? 1 : 0 },
      });
      setTours(res.data.data || []);
      setPage(res.data.page || defaultPage);
      return true;
    } catch (err) {
      if (!silent) showCmsErrorAlert(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    loadTours();
  }, [loadTours]);

  const sortedTours = useMemo(
    () => [...tours].sort((a, b) => b.intTourID - a.intTourID),
    [tours]
  );

  const openModal = (type, row = null) => {
    setActionType(type);
    setSelectedTour(row);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTour(null);
  };

  const closeAndRefresh = async () => {
    closeModal();
    return loadTours(true);
  };

  const toggleStatus = async (tour) => {
    try {
      await axios.post(apiUrl(`/api/tour/status/${tour.intTourID}`), {
        status: !(tour.isActive ?? tour.bitIsActive),
      });
      if (await loadTours(true)) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    }
  };

  const toggleFeatured = async (tour) => {
    try {
      await axios.post(apiUrl(`/api/tour/featured/${tour.intTourID}`), {
        status: !(tour.featured ?? tour.bitFeatured),
      });
      if (await loadTours(true)) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    }
  };

  const archiveTour = async (tour) => {
    if (!window.confirm("Archive this tour package?")) return;

    try {
      await axios.post(apiUrl(`/api/tour/archive/${tour.intTourID}`));
      if (await loadTours(true)) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    }
  };

  const restoreTour = async (tour) => {
    try {
      await axios.post(apiUrl(`/api/tour/restore/${tour.intTourID}`));
      if (await loadTours(true)) showCmsSuccessAlert();
    } catch (err) {
      showCmsErrorAlert(err);
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Tour Page Manager</h4>
          <p className="text-muted mb-0">
            Manage the tour banner and the packages visitors see on the Tours page.
          </p>
        </div>

        <div className="d-flex gap-2">
          {!showArchived && (
            <button className="btn btn-primary" onClick={() => openModal("add")}>
              Add Package
            </button>
          )}
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowArchived((current) => !current)}
          >
            {showArchived ? "Active Packages" : "Archived Packages"}
          </button>
        </div>
      </div>

      <div className="alert alert-light border">
        <div className="fw-semibold mb-1">How this page works</div>
        <div className="small text-muted">
          First update the page hero if needed. Then add or edit tour packages. Each package
          becomes one public tour card and one detail page for visitors.
        </div>
      </div>

      <PageSettingsForm page={page} onSuccess={loadTours} />

      <div className="card">
        <div className="card-header fw-bold">Tour Packages</div>
        <div className="card-body table-responsive">
          {loading ? (
            <div className="p-3">Loading...</div>
          ) : (
            <table className="table table-bordered align-middle">
              <thead>
                <tr>
                  <th width="90">ID</th>
                  <th>Package</th>
                  <th width="140">Location</th>
                  <th width="150">Price</th>
                  <th width="140">Website</th>
                  <th width="120">Featured</th>
                  <th width="320">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTours.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted">
                      No tour packages found.
                    </td>
                  </tr>
                )}

                {sortedTours.map((tour) => (
                  <tr key={tour.intTourID}>
                    <td className="fw-semibold text-muted">{tour.intTourID}</td>
                    <td>
                      <div className="fw-semibold">{tour.title || tour.vchTitle}</div>
                      {tour.summary && <div className="text-muted small mt-1">{tour.summary}</div>}
                    </td>
                    <td>{tour.location || tour.vchLocation}</td>
                    <td>{formatPrice(tour.price || tour.intPrice)}</td>
                    <td>
                      {!showArchived ? (
                        <span className={`badge ${(tour.isActive ?? tour.bitIsActive) ? "bg-success" : "bg-secondary"}`}>
                          {(tour.isActive ?? tour.bitIsActive) ? "Published" : "Hidden"}
                        </span>
                      ) : (
                        <span className="badge bg-dark">Archived</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${(tour.featured ?? tour.bitFeatured) ? "bg-warning text-dark" : "bg-light text-dark border"}`}>
                        {(tour.featured ?? tour.bitFeatured) ? "Featured" : "Standard"}
                      </span>
                    </td>
                    <td>
                      {!showArchived ? (
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openModal("view", tour)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => openModal("edit", tour)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => toggleStatus(tour)}
                          >
                            {(tour.isActive ?? tour.bitIsActive) ? "Hide" : "Publish"}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-dark"
                            onClick={() => toggleFeatured(tour)}
                          >
                            {(tour.featured ?? tour.bitFeatured) ? "Unfeature" : "Feature"}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => archiveTour(tour)}
                          >
                            Archive
                          </button>
                        </div>
                      ) : (
                        <button className="btn btn-sm btn-success" onClick={() => restoreTour(tour)}>
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {actionType === "add"
                      ? "Add Tour Package"
                      : actionType === "edit"
                        ? "Edit Tour Package"
                        : "Tour Package Details"}
                  </h5>
                  <button className="btn-close" onClick={closeModal} />
                </div>
                <div className="modal-body">
                  {actionType === "view" ? (
                    <TourSummaryView tour={selectedTour} />
                  ) : (
                    <TourForm
                      editData={actionType === "edit" ? selectedTour : null}
                      onSuccess={closeAndRefresh}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default AdminTours;
